import { useState, useEffect, useCallback } from 'react'
import Api, { ApiResponseCodes } from '../utils/api'

interface FetchDataProps<T> {
  url: string
  options?: RequestInit
  initialLoading?: boolean
  auto?: boolean
  onSuccess?: (json: T) => void
  onError?: () => void
}

function useFetchData<T>({
  url,
  options = {},
  initialLoading = false,
  auto = true,
  onSuccess,
  onError
}: FetchDataProps<T>) {
  const [response, setResponse] = useState<T | null>(null)
  const [hasError, setHasError] = useState(false)
  const [isLoading, setIsLoading] = useState(initialLoading)

  const fetchData = useCallback(
    async (customUrl?: string) => {
      const finalUrl = customUrl ?? url
      if (!finalUrl) {
        return
      }
      setIsLoading(true)
      setResponse(null)
      setHasError(false)
      try {
        const res = await Api.fetch(finalUrl, options)
        if (res?.status === ApiResponseCodes.RC200) {
          const json = await res.json()
          setResponse(json)
          onSuccess?.(json)
        } else {
          setResponse(null)
          setHasError(true)
          onError?.()
        }
      } catch {
        setResponse(null)
        setHasError(true)
        onError?.()
      } finally {
        setIsLoading(false)
      }
    },
    // Intentionally excluding options, onSuccess, onError from deps:
    // Including them would recreate fetchData on every render (inline objects
    // are new references each time), triggering the auto useEffect in a loop.
    // Callers must pass stable references (constants or values wrapped in
    // useCallback/useMemo).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [url]
  )

  useEffect(() => {
    if (auto) {
      fetchData()
    }
  }, [auto, fetchData])

  return { response, hasError, isLoading, fetchData }
}

export default useFetchData
